import React, { useState, useEffect } from 'react';
import { Info, Copy, Check } from 'lucide-react';
import { ApiService } from '../services/apiService';
import { getVersionAge } from '../utils/timeAgo';

interface VersionInfo {
  version: string;
  containerVersion: string;
  assemblyVersion: string;
  timestamp: string;
}

export const AppVersionButton: React.FC = () => {
  const [backendVersion, setBackendVersion] = useState<VersionInfo | null>(null);
  const [frontendVersion, setFrontendVersion] = useState<string>('unknown');
  const [showTooltip, setShowTooltip] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchVersions = async () => {
      try {
        // Fetch backend version from API
        const apiService = ApiService.getInstance();
        const info = await apiService.getVersion();
        setBackendVersion(info);

        // Get frontend version from environment variable (set during build)
        const uiVersion = import.meta.env.VITE_CONTAINER_VERSION || 'dev';
        setFrontendVersion(uiVersion);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch version');
      }
    };

    fetchVersions();
  }, []);

  const handleCopyVersions = async () => {
    const versionText = `Frontend: ${frontendVersion}\nBackend: ${backendVersion?.containerVersion || 'unknown'}`;
    try {
      await navigator.clipboard.writeText(versionText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy versions:', err);
    }
  };

  return (
    <div data-component="AppVersionButton" className="relative">
      <button
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center"
        title="Application Version"
      >
        <Info className="w-4 h-4 text-gray-600" />
      </button>

      {showTooltip && (
        <div 
          data-flow="tooltip"
          className="absolute top-full right-0 pt-2 z-50"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 min-w-[280px]">
          {error ? (
            <div className="text-sm text-red-600">{error}</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                <div className="text-xs font-semibold text-gray-700">
                  Application Versions
                </div>
                <button
                  onClick={handleCopyVersions}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                  title="Copy both versions"
                >
                  {copied ? (
                    <>
                      <Check className="w-3 h-3 text-green-600" data-flow="copied" />
                      <span className="text-green-600">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" data-flow="idle" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
              
              {/* Frontend Version */}
              <div className="space-y-1">
                <div className="text-xs font-medium text-blue-700">Frontend (UI)</div>
                <div className="text-xs text-gray-600 font-mono bg-gray-50 px-2 py-1 rounded select-text">
                  {frontendVersion}
                </div>
                {getVersionAge(frontendVersion) && (
                  <div className="text-xs text-gray-500 italic">
                    {getVersionAge(frontendVersion)}
                  </div>
                )}
              </div>

              {/* Backend Version */}
              {backendVersion ? (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-green-700">Backend (API)</div>
                  <div className="text-xs text-gray-600 font-mono bg-gray-50 px-2 py-1 rounded select-text">
                    {backendVersion.containerVersion}
                  </div>
                  {getVersionAge(backendVersion.containerVersion) && (
                    <div className="text-xs text-gray-500 italic">
                      {getVersionAge(backendVersion.containerVersion)}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-500">Loading backend version...</div>
              )}
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
};
